import { button, setText } from "./dom";

export interface Tabs<T extends string> {
  tablist: HTMLElement;
  /** One empty panel per tab, for the caller to fill and place. */
  panels: Record<T, HTMLElement>;
  /** Shows the selected tab and panel and sets the tab labels. */
  sync(selected: T, labels: Record<T, string>): void;
  focus(id: T): void;
}

/**
 * An ARIA tablist with one panel per id; ← and → select and focus the neighbouring tab.
 * Ids are `<prefix>-tab-<id>` and `<prefix>-tabpanel-<id>`, classes `<prefix>-tabs` and `<prefix>-tabpanel`.
 */
export function mountTabs<T extends string>(
  ids: readonly T[],
  prefix: string,
  onSelect: (id: T) => void,
): Tabs<T> {
  const tablist = document.createElement("div");
  tablist.className = `${prefix}-tabs`;
  tablist.setAttribute("role", "tablist");
  const tabs = {} as Record<T, HTMLButtonElement>;
  const panels = {} as Record<T, HTMLElement>;
  for (const id of ids) {
    const t = button("", () => onSelect(id));
    t.id = `${prefix}-tab-${id}`;
    t.setAttribute("role", "tab");
    t.setAttribute("aria-controls", `${prefix}-tabpanel-${id}`);
    const p = document.createElement("div");
    p.id = `${prefix}-tabpanel-${id}`;
    p.className = `${prefix}-tabpanel`;
    p.setAttribute("role", "tabpanel");
    p.setAttribute("aria-labelledby", t.id);
    tabs[id] = t;
    panels[id] = p;
    tablist.append(t);
  }
  let selected = ids[0];
  tablist.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const step = e.key === "ArrowRight" ? 1 : ids.length - 1;
    const next = ids[(ids.indexOf(selected) + step) % ids.length];
    onSelect(next);
    tabs[next].focus();
    e.preventDefault();
  });

  return {
    tablist,
    panels,
    sync(id, labels) {
      selected = id;
      for (const tab of ids) {
        const on = tab === id;
        setText(tabs[tab], labels[tab]);
        tabs[tab].setAttribute("aria-selected", String(on));
        tabs[tab].tabIndex = on ? 0 : -1;
        panels[tab].hidden = !on;
      }
    },
    focus: (id) => tabs[id].focus(),
  };
}
